import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { PapiDeviation } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import {
  BiData,
  type DreamupUpscaleConfirmBiEvent,
  type BuyCoreButtonBiEvent,
  type DreamupRemoveBackgroundConfirmBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { pushPointsModal } from '@wix/da-dreamup/pkg/redux/form/actions';
import { getPricePerPrompt } from '@wix/da-dreamup/pkg/redux/form/selectors';
import { jumpToDreamupHeader } from '../../Header';
import { SpendType } from '../useSpendType';
import {
  SpendConfirmationActionType,
  useSpendConfirmationAction,
} from '../useSpendConfirmationAction';

function getBiForActionAndSpendType(
  confirmationAction: SpendConfirmationActionType,
  spendType: SpendType,
  deviation: PapiDeviation,
  biDataComponent: string
) {
  switch (spendType) {
    case SpendType.UpgradeToCore:
      return BiData<BuyCoreButtonBiEvent>({
        evid: 375,
        typeid: deviation.typeId,
        itemid: deviation.deviationId,
        referral_info:
          confirmationAction === SpendConfirmationActionType.Upscale
            ? ReferralType.DREAMUP_SPEND_UPSCALE_CONFIRMATION
            : ReferralType.DREAMUP_SPEND_REMOVE_BG_CONFIRMATION,
      });
    case SpendType.PurchasePoints:
      return undefined;
    case SpendType.SubmitWithPrompt:
    case SpendType.SubmitWithPoints:
    default:
      switch (confirmationAction) {
        case SpendConfirmationActionType.Upscale:
          return BiData<DreamupUpscaleConfirmBiEvent>({
            evid: 929,
            prompt: deviation.extended?.dreamsofart?.prompt,
            deviationid: deviation.deviationId,
            component: biDataComponent,
          });
        case SpendConfirmationActionType.RemoveBackground:
          return BiData<DreamupRemoveBackgroundConfirmBiEvent>({
            evid: 941,
            prompt: deviation.extended?.dreamsofart?.prompt,
            deviationid: deviation.deviationId,
            component: biDataComponent,
          });
      }
  }
}

interface Props {
  deviation: PapiDeviation;
  confirmationActionType: SpendConfirmationActionType;
  spendType: SpendType;
  closeCallback:
    | ((
        event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent
      ) => void)
    | undefined;
  biDataComponent: string;
}

const SpendConfirmationButton: FC<Props> = ({
  deviation,
  confirmationActionType,
  spendType,
  closeCallback,
  biDataComponent,
}) => {
  const { t } = useTranslation();
  const performDesiredAction = useSpendConfirmationAction(
    confirmationActionType,
    deviation
  );
  const dispatchPushPointsModal = useAction(pushPointsModal);
  const pricePerPrompt = useSelector(getPricePerPrompt);

  const [label, handleClick] = useMemo(() => {
    switch (spendType) {
      case SpendType.SubmitWithPoints:
        return [
          t(
            `dreamup.modal.spendConfirmation.confirmButton.confirmWithPoints.${confirmationActionType}`,
            {
              cost: pricePerPrompt,
            }
          ),
          e => {
            performDesiredAction(true);
            closeCallback && closeCallback(e);
          },
        ];
      case SpendType.UpgradeToCore:
        return [
          t(
            `dreamup.modal.spendConfirmation.confirmButton.upgradeToCore.${confirmationActionType}`
          ),
          () =>
            (window.location.href = Url.coreMembershipLink({
              referer: Url.dreamupLink(),
            })),
        ];
      case SpendType.PurchasePoints:
        return [
          t(
            `dreamup.modal.spendConfirmation.confirmButton.buyPoints.${confirmationActionType}`,
            {
              cost: pricePerPrompt,
            }
          ),
          dispatchPushPointsModal,
        ];
      case SpendType.SubmitWithPrompt:
      default:
        return [
          t(
            `dreamup.modal.spendConfirmation.confirmButton.confirmWithPrompt.${confirmationActionType}`
          ),
          e => {
            performDesiredAction(false);
            closeCallback && closeCallback(e);
            jumpToDreamupHeader();
          },
        ];
    }
  }, [
    t,
    confirmationActionType,
    spendType,
    performDesiredAction,
    dispatchPushPointsModal,
    closeCallback,
    pricePerPrompt,
  ]);

  return (
    <BrandedButton
      size="large"
      onClick={handleClick}
      variant={
        spendType === SpendType.UpgradeToCore ? 'core-orange' : 'brand-green'
      }
      biData={getBiForActionAndSpendType(
        confirmationActionType,
        spendType,
        deviation,
        biDataComponent
      )}
    >
      {label}
    </BrandedButton>
  );
};

SpendConfirmationButton.displayName = 'SpendConfirmationButton';

export default SpendConfirmationButton;
